/**
 * The confirm queue's rules, with no React: what each card offers and
 * what the header does.
 *
 *   isChoiceCard(p)      true when a card has more than one way to confirm
 *   isOwnConfirmCard(p)  true for a schedule change, which pauses visits
 *   cardActions(p, e)    one button per outcome, primary first, then the
 *                        secondaries, then Dismiss; each names its outcome
 *                        and carries the row it targets, if any
 *   cardFacts(p, e)      the card's labelled facts: a name as the record
 *                        will store it, a one-line value with its capital
 *                        and its field's abbreviations in their standard
 *                        form, a note, an address or an email as written;
 *                        a yes-or-no role is never a fact
 *   billToLeftToOffice(p, e, may) true for a contact card that would mark
 *                        the billing contact, seen by a seat that may not
 *                        choose who receives the bill
 *   appliesOnCard(p, e, field, fs) true when a field applies to the card
 *                        as it stands: the registry's rule (the
 *                        code-status value only while the instrument
 *                        carries one) and the record form's (an
 *                        appointment's one date, with its status); the
 *                        editor, the facts and the confirm payload all
 *                        read it
 *   cardFieldLabel(t, f) a field's label on a card: the record form's
 *   cardDefault(t, f)    the value a field takes when the proposal has
 *                        none, as the writer takes it
 *   bulkAction(input)    the header's button text, the cards it adds and
 *                        the quiet line counting what it leaves
 *
 * A held row (status held_for_review) is a record update Olive could not
 * read cleanly. Its rules:
 *
 *   isHeldForReview(p)   true for a held row
 *   heldSentence(p)      the one sentence its card shows, never the
 *                        stored reason
 *   cleanHeldProposal(p) the row holding only its form fields, each kept
 *                        only when its stored value is valid for the field,
 *                        so what the card shows is what a confirm sends
 *   setAsideOf(p)        what cleaning dropped, in plain words, for the
 *   setAsideLine(e)      "Set aside:" line
 *   heldMissing(p, e)    the required fields it still lacks, and the line
 *   heldMissingLine(p, e) naming them; its outcomes wait until none is left
 *   heldFailureLine(...) the card's line when the server still refuses a
 *                        confirm, naming the field in plain words
 *
 * A held row with no editor (a SPICE or unknown type) offers Dismiss
 * only. The header never adds a held row and counts it as "Olive
 * couldn't read".
 *
 * A choice card is a medication add or change with a medication of the
 * same name on the record ("Replace existing dose" or "Keep both"), any
 * medication card with a look-alike on the record, or a condition card
 * with a similar condition on the record. Each outcome is its own button
 * and the header never decides one. A condition whose name is already on
 * the record is not a choice: the record joins it on confirm.
 *
 * Labels and facts read the effective values: the care manager's Adjust
 * edit where one exists, else the proposal's payload. Edits are keyed
 * `${proposalId}:${field}`; a set (a medication's When) is kept as its
 * tokens comma-joined in the vocabulary's order.
 *
 * A typed card is its record form's card: its words, its value words,
 * which fields show for a value and which a value requires all come from
 * the form's own table (recordFormWords.ts), through the form the type
 * is confirmed into (recordProjectionForType).
 */

import type { ConditionMatchRow, MedicationMatchRow, ProposalWire } from '@/lib/notes';
import {
  editableFields,
  fieldApplies,
  formFields,
  specForType,
  type PayloadFieldSpec,
} from '@/lib/noteVocabulary';
import { factLabelOverride, fieldLabel, humanizeToken } from '@/lib/fieldLabels';
import {
  browserApplies,
  recordFieldLabel,
  recordProjectionForType,
  valueLabel,
} from '@/lib/recordFormWords';
import { fieldSentence, ruleFromSpec } from '@/lib/fieldErrorCopy';
import { errorCodeOf, fieldOf } from '@/lib/errorCopy';
import { fmtTime, proseDate } from '@/lib/proseDate';
import { capitalizeFirst } from '@/lib/capitalizeFirst';
import { displayValue, nameCase as toNameCase } from '@/lib/valueCase';
import { formatPhoneDisplay } from '@/lib/phone';

/** 'HH:MM' (24-hour), seconds allowed: a time field's value. */
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/;

export type CardActionKind = 'confirm' | 'keep_both' | 'update_row' | 'dismiss';

export interface CardAction {
  kind: CardActionKind;
  variant: 'primary' | 'secondary' | 'ghost';
  /** Full label; always the accessible name. */
  label: string;
  /** Only the Replace button has one: 'Replace existing'. */
  shortLabel?: string;
  /** Sent as target_row_id; absent means none is sent. */
  targetRowId?: string;
  testId: string;
}

export interface CardFact {
  key: string;
  label: string;
  value: string;
}

type Edits = Readonly<Record<string, string>>;

const MEDICATION_ADD_TYPES = new Set(['medication_add', 'medication_change']);
const MEDICATION_TYPES = new Set([...MEDICATION_ADD_TYPES, 'medication_discontinue']);
const CONDITION_TYPES = new Set(['condition_add', 'condition_change']);

/** The primary's label for a type whose outcome needs no name or match. */
const PLAIN_PRIMARY_LABELS: Record<string, string> = {
  appointment_upcoming: 'Add appointment',
  appointment_past: 'Record appointment',
  provider_new: 'Add to care team',
  housing_change: 'Update housing',
  risk_change: 'Update risk',
  directive_change: 'Update directive',
  coverage_change: 'Update insurance',
  functional_status_change: 'Update activity',
  allergy_change: 'Add allergy',
  spice_activity: 'Add to the session',
  spice_session_summary: 'Add to the session',
  spice_decline: 'Add to the session',
  spice_next_plan: 'Add to the session',
  spice_status_change: 'Pause visits',
  spice_care_notice: 'Send to the care manager',
};

const HOSPITAL_KIND_LABELS: Record<string, string> = {
  er_visit: 'Record ER visit',
  admission: 'Record admission',
  discharge: 'Record discharge',
  rehab_admission: 'Record rehab admission',
  rehab_discharge: 'Record rehab discharge',
};

const REPLACE_LABEL = 'Replace existing dose';
const REPLACE_SHORT_LABEL = 'Replace existing';

/** A payload value as display text: strings and numbers only. */
export function scalar(v: unknown): string {
  return typeof v === 'string' || typeof v === 'number' ? String(v) : '';
}

/** A value made readable: registry dates as prose ("July 20, 2026"),
 *  enum tokens through the acronym-aware humanize ("ER visit"). Free
 *  text passes through untouched. */
export function readableValue(v: string, kind: string | undefined): string {
  if (v === '') return v;
  if (kind === 'date' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return proseDate(v);
  if (kind === 'time' && TIME_RE.test(v)) return fmtTime(v);
  if (kind === 'enum' || kind === 'risk_category') return humanizeToken(v);
  return v;
}

/** The value a confirm will write for one field: the edit, else the payload. */
/** A field's value on the card as text: the edit, else the payload's; a
 *  set reads as its tokens comma-joined. */
export function effective(p: ProposalWire, edits: Edits, field: string): string {
  const edit = edits[`${p.id}:${field}`];
  if (edit !== undefined) return edit;
  const v = p.payload[field];
  if (Array.isArray(v)) return v.filter((x) => typeof x === 'string').join(',');
  return scalar(v);
}

/** The value a field takes when the proposal carries none, as the
 *  writer takes it: an appointment's status by its type (scheduled for
 *  an upcoming one, attended for a past one, as proposalHold reads it),
 *  else the registry default, else nothing. */
export function cardDefault(proposalType: string, field: string): string {
  if (field === 'status' && proposalType === 'appointment_upcoming') return 'scheduled';
  if (field === 'status' && proposalType === 'appointment_past') return 'attended';
  return specForType(proposalType)?.[field]?.defaultValue ?? '';
}

/** A field's value as the card's rules read it: the edit, else the
 *  payload's, else the value the writer would take. */
function ruleValue(p: ProposalWire, edits: Edits, field: string): string {
  return effective(p, edits, field) || cardDefault(p.proposal_type, field);
}

/** True when a field applies to the card as it stands, reading the
 *  values a confirm would write, the edit first: the registry's rule (a
 *  field that applies only with another field's value, the code-status
 *  value with the instrument) and the record form's (an appointment's
 *  Scheduled for while it is scheduled, a medication's Ended while it is
 *  discontinued). A field with no rule always applies. */
export function appliesOnCard(p: ProposalWire, edits: Edits, field: string, fs: PayloadFieldSpec): boolean {
  const read = (f: string): string => ruleValue(p, edits, f);
  if (!fieldApplies(fs, read)) return false;
  const projection = recordProjectionForType(p.proposal_type);
  return projection === undefined || browserApplies(projection, field, read);
}

/** A field's label on a card: the record form's word for it. */
export function cardFieldLabel(proposalType: string, field: string): string {
  const projection = recordProjectionForType(proposalType);
  return projection === undefined ? fieldLabel(field) : recordFieldLabel(projection, field);
}

/** A stored token as a card reads it: the record form's word for it. */
export function cardValueLabel(proposalType: string, field: string, token: string): string {
  const projection = recordProjectionForType(proposalType);
  return projection === undefined ? humanizeToken(token) : valueLabel(projection, field, token);
}

function medicationMatch(p: ProposalWire): { exact: MedicationMatchRow | null; near: MedicationMatchRow[] } {
  if (!MEDICATION_TYPES.has(p.proposal_type)) return { exact: null, near: [] };
  return { exact: p.medication_match?.exact ?? null, near: p.medication_match?.near ?? [] };
}

function conditionMatch(p: ProposalWire): { exact: ConditionMatchRow | null; near: ConditionMatchRow[] } {
  if (!CONDITION_TYPES.has(p.proposal_type)) return { exact: null, near: [] };
  return { exact: p.condition_match?.exact ?? null, near: p.condition_match?.near ?? [] };
}

/** A condition's name has been edited away from the proposal's own name
 *  (case and surrounding spaces aside), so the listed match no longer applies. */
function conditionNameEditedAway(p: ProposalWire, edits: Edits): boolean {
  const edit = edits[`${p.id}:name`];
  if (edit === undefined) return false;
  return edit.trim().toLowerCase() !== scalar(p.payload.name).trim().toLowerCase();
}

/** A medication's name as it reads: the medication fields' own forms. */
function medicationName(name: string): string {
  return displayValue(name.trim(), 'medication');
}

/** A condition's name as it reads: the conditions' and tests' own forms. */
function conditionName(name: string): string {
  return displayValue(name.trim(), 'diagnosis');
}

/** A medication on the record as the queue names it: its name, then its dose. */
export function medicationRowValue(row: Pick<MedicationMatchRow, 'name' | 'dose'>): string {
  const dose = row.dose?.trim() ?? '';
  return dose === '' ? medicationName(row.name) : `${medicationName(row.name)} ${displayValue(dose, 'medication')}`;
}

/**
 * A fact's value, by its field: an ISO date in prose and date words with
 * a capital; a token through its label map; an email as written; a name
 * kind as the record will store it (name case); a one-line value with
 * its capital and its field's abbreviations; a note, an address or a
 * phone as written.
 */
function factValue(raw: string, fs: PayloadFieldSpec, type: string, field: string): string {
  if (fs.kind === 'date') return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? proseDate(raw) : displayValue(raw);
  if (fs.kind === 'time') return TIME_RE.test(raw) ? fmtTime(raw) : raw;
  if (fs.kind === 'enum') return cardValueLabel(type, field, raw);
  if (fs.kind === 'enum_set') {
    return raw
      .split(',')
      .filter(Boolean)
      .map((token) => cardValueLabel(type, field, token))
      .join(', ');
  }
  if (fs.kind === 'risk_category') return humanizeToken(raw);
  if (fs.kind === 'email') return raw;
  switch (fs.textKind) {
    case 'person':
    case 'place':
    case 'role':
    case 'relationship':
    case 'carrier':
      return toNameCase(raw, fs.textKind);
    case 'value':
    case 'medication':
    case 'medication_reason':
    case 'diagnosis':
    case 'appointment_purpose':
    case 'allergy':
      return displayValue(raw, fs.textKind);
    case 'phone':
      // The record form's display of a number.
      return formatPhoneDisplay(raw);
    case 'note':
    case 'address_text':
    case 'code':
      return raw;
    default:
      return capitalizeFirst(readableValue(raw, fs.kind));
  }
}

/** "Ruth" from "Ruth Pembrook"; a title keeps the next word ("Dr. Okafor"). */
function firstName(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  const first = words[0]!.endsWith('.') && words.length > 1 ? `${words[0]} ${words[1]}` : words[0]!;
  return capitalizeFirst(first);
}

export function isChoiceCard(p: ProposalWire): boolean {
  const med = medicationMatch(p);
  if (MEDICATION_ADD_TYPES.has(p.proposal_type) && med.exact !== null) return true;
  if (MEDICATION_TYPES.has(p.proposal_type) && med.near.length > 0) return true;
  if (CONDITION_TYPES.has(p.proposal_type) && conditionMatch(p).near.length > 0) return true;
  return false;
}

/** True for a spice_status_change: never decided by the header. */
export function isOwnConfirmCard(p: ProposalWire): boolean {
  return p.proposal_type === 'spice_status_change';
}

function primary(p: ProposalWire, label: string, targetRowId?: string): CardAction {
  return {
    kind: 'confirm',
    variant: 'primary',
    label,
    ...(targetRowId !== undefined ? { targetRowId } : {}),
    testId: `proposal-confirm-${p.id}`,
  };
}

function updateRow(p: ProposalWire, row: { id: string; name: string; kind: 'medication' | 'condition' }): CardAction {
  return {
    kind: 'update_row',
    variant: 'secondary',
    label: `Update ${row.kind === 'condition' ? conditionName(row.name) : medicationName(row.name)}`,
    targetRowId: row.id,
    testId: `proposal-update-${p.id}-${row.id}`,
  };
}

function primaryLabel(p: ProposalWire, edits: Edits): string {
  const type = p.proposal_type;
  if (MEDICATION_ADD_TYPES.has(type)) return 'Add medication';
  if (type === 'medication_discontinue') {
    const name = medicationName(effective(p, edits, 'name'));
    return name === '' ? 'Mark medication stopped' : `Mark ${name} stopped`;
  }
  if (CONDITION_TYPES.has(type)) {
    const exact = conditionMatch(p).exact;
    return exact !== null && !conditionNameEditedAway(p, edits)
      ? `Update ${conditionName(exact.name)}`
      : 'Add condition';
  }
  if (type === 'hospital_event') {
    return HOSPITAL_KIND_LABELS[effective(p, edits, 'kind').trim()] ?? 'Record hospital event';
  }
  if (type === 'contact_change') {
    const first = firstName(effective(p, edits, 'name'));
    return first === '' ? 'Add to contacts' : `Add ${first} to contacts`;
  }
  return PLAIN_PRIMARY_LABELS[type] ?? 'Add to the record';
}

export function cardActions(p: ProposalWire, edits: Edits): CardAction[] {
  const actions: CardAction[] = [];
  if (isHeldForReview(p) && specForType(p.proposal_type) === undefined) {
    return [{ kind: 'dismiss', variant: 'ghost', label: 'Dismiss', testId: `proposal-dismiss-${p.id}` }];
  }
  const med = medicationMatch(p);
  if (MEDICATION_ADD_TYPES.has(p.proposal_type) && med.exact !== null) {
    actions.push({
      kind: 'confirm',
      variant: 'primary',
      label: REPLACE_LABEL,
      shortLabel: REPLACE_SHORT_LABEL,
      targetRowId: med.exact.id,
      testId: `proposal-confirm-${p.id}`,
    });
    actions.push({
      kind: 'keep_both',
      variant: 'secondary',
      label: 'Keep both',
      testId: `proposal-keep-both-${p.id}`,
    });
  } else if (p.proposal_type === 'medication_discontinue') {
    actions.push(primary(p, primaryLabel(p, edits), med.exact?.id));
  } else {
    actions.push(primary(p, primaryLabel(p, edits)));
  }
  for (const row of med.near) actions.push(updateRow(p, { ...row, kind: 'medication' }));
  for (const row of conditionMatch(p).near) actions.push(updateRow(p, { ...row, kind: 'condition' }));
  actions.push({ kind: 'dismiss', variant: 'ghost', label: 'Dismiss', testId: `proposal-dismiss-${p.id}` });
  return actions;
}

/** A contact card that would mark the billing contact, seen by a seat
 *  that cannot: the server confirms it without the flag, and the card
 *  says the office sets who receives the bill. */
export function billToLeftToOffice(p: ProposalWire, edits: Edits, mayChooseBillTo: boolean): boolean {
  if (mayChooseBillTo || p.proposal_type !== 'contact_change') return false;
  const edit = edits[`${p.id}:is_billing`];
  return edit !== undefined ? edit === 'true' : p.payload['is_billing'] === true;
}

/** A stored SPICE token: lowercase words joined by underscores, no spaces. */
const SPICE_TOKEN_RE = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/;

export function cardFacts(p: ProposalWire, edits: Edits): CardFact[] {
  const facts: CardFact[] = [];
  const spec = specForType(p.proposal_type);
  if (spec !== undefined) {
    const fields = editableFields(spec);
    const dateFields = fields.filter(([, fs]) => fs.kind === 'date').map(([field]) => field);
    const hasDate = dateFields.some((field) => effective(p, edits, field).trim() !== '');
    for (const [field, fs] of fields) {
      // A yes-or-no role is a tick in Adjust, never a fact, whether it
      // comes from the payload or from a tick (the text "true" or "false").
      if (fs.kind === 'boolean') continue;
      // The note's own words for a date show only when no date is set.
      if (field === 'date_text' && hasDate) continue;
      // A direct-entry field (an insurance card's fields, the composed
      // code-status set) is typed on the record page; a confirm never
      // writes it, so the card never shows it.
      if (fs.directOnly === true) continue;
      if (!appliesOnCard(p, edits, field, fs)) continue;
      const raw = effective(p, edits, field).trim();
      if (raw === '') continue;
      facts.push({
        key: field,
        label: factLabelOverride(p.proposal_type, field) ?? cardFieldLabel(p.proposal_type, field),
        value: factValue(raw, fs, p.proposal_type, field),
      });
    }
  } else {
    // A type with no registry spec (the SPICE proposals): sentences from a
    // note, with the plain first capital and no dictionary. A stored token
    // (a modality, a status, a topic: one lowercase word or words joined
    // by underscores, no spaces) reads as words: "long_term_memory" is
    // "Long term memory", never the token.
    for (const [key, v] of Object.entries(p.payload)) {
      const raw = scalar(v).trim();
      if (raw === '') continue;
      const value = SPICE_TOKEN_RE.test(raw) ? humanizeToken(raw) : capitalizeFirst(raw);
      facts.push({ key, label: humanizeToken(key), value });
    }
  }

  const med = medicationMatch(p);
  if (med.exact !== null) {
    facts.push({ key: 'on-record', label: 'On record', value: medicationRowValue(med.exact) });
  }
  for (const row of med.near) {
    facts.push({ key: `near-${row.id}`, label: 'Did you mean', value: medicationRowValue(row) });
  }
  const cond = conditionMatch(p);
  if (cond.exact !== null && !conditionNameEditedAway(p, edits)) {
    facts.push({ key: 'on-record', label: 'On record', value: conditionName(cond.exact.name) });
  }
  for (const row of cond.near) {
    facts.push({ key: `near-${row.id}`, label: 'Similar', value: conditionName(row.name) });
  }
  return facts;
}

function count(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * The header's bulk action. It adds the high-confidence cards this viewer
 * may resolve that have one outcome, need no date, are not a schedule
 * change and are not held. The button shows only when it would add two
 * or more; it reads "Add all N" only when N is every card in the queue
 * and at least three. The quiet line counts the high-confidence cards it
 * leaves, by reason: choice, own confirm, date, then held. A held card
 * counts once, as held.
 */
export function bulkAction(input: {
  proposals: readonly ProposalWire[];
  threshold: number;
  canResolve: (p: ProposalWire) => boolean;
  holdFor: (p: ProposalWire) => unknown | null;
}): { actOn: ProposalWire[]; label: string | null; skippedLine: string | null } {
  const { proposals, threshold, canResolve, holdFor } = input;
  const high = proposals.filter((p) => p.confidence >= threshold);
  const actOn = high.filter(
    (p) =>
      canResolve(p) &&
      !isHeldForReview(p) &&
      holdFor(p) === null &&
      !isChoiceCard(p) &&
      !isOwnConfirmCard(p),
  );
  const n = actOn.length;
  if (n < 2) return { actOn, label: null, skippedLine: null };

  const label = n === proposals.length && n >= 3 ? `Add all ${n} to the record` : `Add ${n} to the record`;

  const resolvable = high.filter(canResolve);
  const open = resolvable.filter((p) => !isHeldForReview(p));
  const held = resolvable.length - open.length;
  const choices = open.filter(isChoiceCard).length;
  const own = open.filter(isOwnConfirmCard).length;
  const dated = open.filter(
    (p) => !isChoiceCard(p) && !isOwnConfirmCard(p) && holdFor(p) !== null,
  ).length;
  const parts: string[] = [];
  if (choices > 0) parts.push(count(choices, 'needs your choice', 'need your choice'));
  if (own > 0) parts.push(count(own, 'to confirm on its own', 'to confirm on their own'));
  if (dated > 0) parts.push(count(dated, 'needs a date', 'need a date'));
  if (held > 0) parts.push(`${held} Olive couldn't read`);
  return { actOn, label, skippedLine: parts.length > 0 ? parts.join(', ') : null };
}

// ---------------------------------------------------------------------
// Held rows
// ---------------------------------------------------------------------

const TYPED_HELD_SENTENCE =
  "Part of this update couldn't be read clearly. Check it under Adjust and fix what's wrong, or press Dismiss.";
const UNTYPED_HELD_SENTENCE =
  "Part of this update couldn't be read clearly, and it can't be adjusted here. Press Dismiss, then add it to the record yourself if it's right.";
const STILL_REFUSED_LINE = "This update still couldn't be added. Check each box above, or press Dismiss.";

/** The label that opens the set-aside line. */
export const SET_ASIDE_LABEL = 'Set aside:';

/** A registry date, with or without a time after it (the server's rule). */
const DATE_PREFIX_RE = /^\d{4}-\d{2}-\d{2}(?:[T ].*)?$/;

/** True for a row Olive could not read cleanly (status held_for_review). */
export function isHeldForReview(p: ProposalWire): boolean {
  return p.status === 'held_for_review';
}

/** The one sentence a held card shows. Never the stored reason. */
export function heldSentence(p: ProposalWire): string {
  return specForType(p.proposal_type) !== undefined ? TYPED_HELD_SENTENCE : UNTYPED_HELD_SENTENCE;
}

/** The stored value as its field keeps it, or undefined when it is not
 *  valid for the field. */
function keptValue(v: unknown, fs: PayloadFieldSpec): unknown {
  switch (fs.kind) {
    case 'string':
    case 'email':
    case 'risk_category':
      if (typeof v === 'string') return v.trim() === '' ? undefined : v;
      if (typeof v === 'number' && Number.isFinite(v)) return String(v);
      return undefined;
    case 'date':
      return typeof v === 'string' && DATE_PREFIX_RE.test(v) ? v : undefined;
    case 'time':
      return typeof v === 'string' && TIME_RE.test(v.trim()) ? v.trim() : undefined;
    case 'enum':
      return typeof v === 'string' && (fs.values ?? []).includes(v) ? v : undefined;
    case 'boolean':
      return typeof v === 'boolean' ? v : undefined;
    default:
      return undefined;
  }
}

/** A held typed row holding only its form fields, each kept only when its
 *  stored value is valid for that field and the field applies among the
 *  kept values; any other row returned as is. */
export function cleanHeldProposal(p: ProposalWire): ProposalWire {
  if (!isHeldForReview(p)) return p;
  const spec = specForType(p.proposal_type);
  if (spec === undefined) return p;
  const payload: Record<string, unknown> = {};
  for (const [field, fs] of formFields(spec)) {
    const kept = keptValue(p.payload[field], fs);
    if (kept !== undefined) payload[field] = kept;
  }
  for (const [field, fs] of formFields(spec)) {
    if (!fieldApplies(fs, (name) => payload[name])) delete payload[field];
  }
  return { ...p, payload };
}

/** What cleaning dropped from a held typed row, in plain words. */
export interface SetAsideEntry {
  key: string;
  label: string;
  value: string;
}

function carriesNothing(v: unknown): boolean {
  return (
    v === null ||
    v === undefined ||
    (typeof v === 'string' && v.trim() === '') ||
    (Array.isArray(v) && v.length === 0)
  );
}

/** A stored value in plain words; an object reads as nothing (its label
 *  alone is listed). */
function plainValue(v: unknown, fs: PayloadFieldSpec | undefined): string {
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number') return String(v);
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (Array.isArray(v) && v.every((entry) => typeof entry === 'string' || typeof entry === 'number')) {
    const tokens = fs?.kind === 'enum_set' && fs.directOnly === true;
    return v.map((entry) => (tokens ? humanizeToken(String(entry)) : String(entry))).join(', ');
  }
  return '';
}

export function setAsideOf(p: ProposalWire): SetAsideEntry[] {
  if (!isHeldForReview(p)) return [];
  const spec = specForType(p.proposal_type);
  if (spec === undefined) return [];
  const kept = cleanHeldProposal(p).payload;
  const entries: SetAsideEntry[] = [];
  for (const [key, v] of Object.entries(p.payload)) {
    if (Object.prototype.hasOwnProperty.call(kept, key)) continue;
    if (carriesNothing(v)) continue;
    if (spec[key]?.kind === 'uuid') continue;
    entries.push({ key, label: fieldLabel(key), value: plainValue(v, spec[key]) });
  }
  return entries;
}

export function setAsideLine(entries: readonly SetAsideEntry[]): string | null {
  if (entries.length === 0) return null;
  const parts = entries.map((e) => (e.value === '' ? e.label : `${e.label} ${e.value}`));
  return `${SET_ASIDE_LABEL} ${parts.join('; ')}`;
}

/** "A", "A and B", "A, B and C". */
function andList(values: readonly string[]): string {
  if (values.length <= 1) return values[0] ?? '';
  return `${values.slice(0, -1).join(', ')} and ${values[values.length - 1]}`;
}

/** The required form fields a held row still lacks, in registry order. */
export function heldMissing(p: ProposalWire, edits: Edits): string[] {
  if (!isHeldForReview(p)) return [];
  const spec = specForType(p.proposal_type);
  if (spec === undefined) return [];
  const cleaned = cleanHeldProposal(p);
  const missing: string[] = [];
  for (const [field, fs] of formFields(spec)) {
    if (fs.required !== true || fs.kind === 'boolean') continue;
    if (!appliesOnCard(cleaned, edits, field, fs)) continue;
    if (effective(cleaned, edits, field).trim() === '') missing.push(field);
  }
  return missing;
}


export function heldMissingLine(p: ProposalWire, edits: Edits): string | null {
  const missing = heldMissing(p, edits);
  if (missing.length === 0) return null;
  const spec = specForType(p.proposal_type)!;
  const choose = missing.every((f) => spec[f]?.kind === 'enum' || spec[f]?.kind === 'risk_category');
  return `${choose ? 'Choose' : 'Fill in'} ${andList(missing.map((f) => cardFieldLabel(p.proposal_type, f)))} first.`;
}

function isBlank(v: unknown): boolean {
  return v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
}

/** True when the sent value breaks a rule the browser can check. */
function breaksBrowserRule(v: unknown, fs: PayloadFieldSpec): boolean {
  if (isBlank(v)) return fs.required === true;
  if (fs.kind === 'enum') return typeof v !== 'string' || !(fs.values ?? []).includes(v);
  if (fs.kind === 'date') return typeof v !== 'string' || !DATE_PREFIX_RE.test(v);
  if (fs.kind === 'time') return typeof v !== 'string' || !TIME_RE.test(v.trim());
  return false;
}

/** The card's line after the server refuses a held confirm with a 400:
 *  the field the value sent breaks, else the field the refusal names,
 *  else the tenant's risk list, else a line asking to check each field. */
export function heldFailureLine(
  p: ProposalWire,
  sent: Readonly<Record<string, unknown>>,
  error: unknown,
): string {
  const spec = specForType(p.proposal_type);
  if (spec === undefined) return STILL_REFUSED_LINE;
  const fields = formFields(spec);
  for (const [field, fs] of fields) {
    const rule = ruleFromSpec(fs);
    if (rule !== null && breaksBrowserRule(sent[field], fs)) {
      return fieldSentence(fieldLabel(field), rule, sent[field]);
    }
  }
  const code = errorCodeOf(error);
  const named = fieldOf(error);
  if ((code === 'invalid_value' || code === 'invalid_field') && named !== null) {
    const hit = fields.find(([field]) => field === named);
    const rule = hit !== undefined ? ruleFromSpec(hit[1]) : null;
    if (rule !== null) return fieldSentence(fieldLabel(named), rule, sent[named]);
  }
  const risk = fields.find(([, fs]) => fs.kind === 'risk_category');
  if (risk !== undefined) return fieldSentence(fieldLabel(risk[0]), { kind: 'from_list' });
  return STILL_REFUSED_LINE;
}
